import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { PhoneCall, MessageCircle, FileText, ArrowUpRight, Instagram, Facebook, Star, MapPin, ShieldCheck, BadgeCheck } from "lucide-react";
import api from "../lib/api";
import Seo from "../components/Seo";
import { Reveal, Overline } from "../components/Reveal";
import { CrownMark } from "../components/CrownMark";
import AssessmentForm from "../components/AssessmentForm";
import { TESTIMONIALS } from "../data/content";
import { waLink } from "../data/showcase";
import { AUDIENCES, AUDIENCE_CHIPS, DEFAULT_AUDIENCE, PORTFOLIO_URL, WORK, projectTiles, orderForAudience } from "../data/portfolio";
import { PHONE_DISPLAY, PHONE_TEL, INSTAGRAM_URL, INSTAGRAM_HANDLE, FACEBOOK_URL } from "../data/contact";
import { responsive } from "../lib/img";

const DOMAIN = "https://www.speedyresurfacing.com.au";

const Tile = ({ t, i, big }) => (
  <Link to={t.to} data-testid={`portfolio-tile-${i}`}
        className={`group relative block overflow-hidden bg-obsidian-800 ${big ? "col-span-2 row-span-2" : ""}`}>
    <img {...responsive(t.src, big ? "(min-width: 1024px) 600px, 66vw" : "(min-width: 1024px) 300px, 33vw", big ? [640, 1000, 1200] : [320, 480, 640])}
         alt={`${t.title} — ${t.sport} court resurfacing by Speedy Resurfacing`} loading={i < 4 ? "eager" : "lazy"} decoding="async"
         className="aspect-square h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.06]" />
    <div className="absolute inset-0 flex flex-col justify-end bg-gradient-to-t from-obsidian/90 via-obsidian/20 to-transparent p-3 opacity-0 transition-opacity duration-300 group-hover:opacity-100 md:p-4">
      <span className="font-mono text-[9px] uppercase tracking-[0.3em] text-gold">{t.sport} · {t.place}</span>
      <span className="mt-1 font-heading font-bold text-sm leading-tight text-white md:text-base">{t.title}</span>
    </div>
    <span className="pointer-events-none absolute right-2 top-2 rounded-sm bg-obsidian/60 p-1 backdrop-blur-sm"><CrownMark className="h-3.5" animate={false} /></span>
  </Link>
);

export default function Portfolio() {
  const [params] = useSearchParams();
  const key = (params.get("for") || "").toLowerCase();
  const aud = AUDIENCES[key] || null;
  const v = aud || DEFAULT_AUDIENCE;
  const audKey = aud ? Object.keys(AUDIENCES).find((k) => AUDIENCES[k] === aud) : null;
  const [projects, setProjects] = useState([]);
  useEffect(() => { api.get("/projects").then((r) => setProjects(r.data)).catch(() => {}); }, []);
  const tiles = useMemo(() => orderForAudience([...WORK, ...projectTiles(projects)], audKey), [projects, audKey]);
  const wa = waLink(v.whatsapp);

  return (
    <div className="noise" data-testid="portfolio-page" data-for={audKey || undefined}>
      <Seo title={`${v.seo} | Speedy Resurfacing`}
           description={`${v.pitch} Call 0466 495 495 or request a free site assessment.`}
           path={PORTFOLIO_URL}
           jsonLd={{
             "@context": "https://schema.org",
             "@type": "CollectionPage",
             name: `${v.seo} | Speedy Resurfacing`,
             url: DOMAIN + PORTFOLIO_URL,
             description: v.pitch,
             publisher: { "@type": "Organization", name: "Speedy Resurfacing", url: DOMAIN, sameAs: [INSTAGRAM_URL, FACEBOOK_URL] },
             mainEntity: { "@type": "ImageGallery", name: "Speedy Resurfacing court portfolio",
               associatedMedia: tiles.slice(0, 12).map((t) => ({ "@type": "ImageObject", contentUrl: DOMAIN + t.src, name: t.title, description: `${t.sport} · ${t.place}` })) },
           }} />

      <section className="relative overflow-hidden pb-14 pt-28 md:pb-20 md:pt-36" data-testid="portfolio-hero">
        <div className="stage-spot stage-spot-a" aria-hidden="true" />
        <div className="relative mx-auto max-w-7xl px-5 md:px-10">
          <p className="stage-in font-mono text-[11px] uppercase tracking-[0.45em] text-gold" style={{ animationDelay: "0.1s" }}>
            Portfolio · {v.label} · Merrylands HQ · Sydney &amp; NSW
          </p>
          <h1 className="mt-6 font-heading font-black text-4xl leading-[0.9] tracking-tighter sm:text-5xl lg:text-7xl" data-testid="portfolio-title">
            <span className="stage-in block" style={{ animationDelay: "0.3s" }}>{v.h1}</span>
            <span className="stage-in gold-text block" style={{ animationDelay: "0.5s" }}>{v.h2}</span>
          </h1>
          <p className="stage-in mt-7 max-w-2xl text-base text-zinc-300 md:text-lg" style={{ animationDelay: "0.8s" }} data-testid="portfolio-pitch">{v.pitch}</p>
          <div className="stage-in mt-9 flex flex-wrap gap-3" style={{ animationDelay: "1s" }}>
            <a href={PHONE_TEL} data-testid="portfolio-call" className="inline-flex items-center gap-2 rounded-sm bg-gold px-7 py-4 font-heading font-black text-obsidian shadow-gold transition-colors hover:bg-gold-hover">
              <PhoneCall size={18} /> Call {PHONE_DISPLAY}
            </a>
            <a href={wa} target="_blank" rel="noopener noreferrer" data-testid="portfolio-whatsapp" className="inline-flex items-center gap-2 rounded-sm border border-neon/50 px-7 py-4 font-bold text-neon transition-colors hover:border-neon hover:bg-neon/10">
              <MessageCircle size={18} /> WhatsApp us
            </a>
            <Link to="/quote" state={{ sport: v.sport, source: "portfolio" }} data-testid="portfolio-quote" className="inline-flex items-center gap-2 rounded-sm border border-white/20 px-7 py-4 font-bold text-white transition-colors hover:border-gold hover:text-gold">
              <FileText size={18} /> Get a Quote
            </Link>
          </div>
          <nav className="stage-in mt-10 -mx-5 overflow-x-auto px-5 md:mx-0 md:px-0" aria-label="Who we work with" style={{ animationDelay: "1.2s" }} data-testid="portfolio-chips">
            <div className="flex w-max gap-2">
              <Link to={PORTFOLIO_URL} replace data-testid="portfolio-chip-all" className={`whitespace-nowrap rounded-full border px-4 py-2 text-sm font-bold transition-colors ${!aud ? "border-gold bg-gold text-obsidian" : "border-white/15 text-zinc-200 hover:border-gold"}`}>All work</Link>
              {AUDIENCE_CHIPS.map(([k, label]) => (
                <Link key={k} to={`?for=${k}`} replace data-testid={`portfolio-chip-${k}`}
                      className={`whitespace-nowrap rounded-full border px-4 py-2 text-sm font-bold transition-colors ${AUDIENCES[k] === aud ? "border-gold bg-gold text-obsidian" : "border-white/15 text-zinc-200 hover:border-gold"}`}>
                  {label}
                </Link>
              ))}
            </div>
          </nav>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 pb-20 md:px-10" data-testid="portfolio-grid-section">
        <div className="flex flex-wrap items-center justify-between gap-4 border-y border-white/10 py-5" data-testid="portfolio-ig-header">
          <div className="flex items-center gap-4">
            <span className="flex h-14 w-14 items-center justify-center rounded-full border-2 border-gold/70 bg-obsidian-800 shadow-gold"><CrownMark className="h-7" /></span>
            <div>
              <p className="font-heading font-black text-lg leading-none">{INSTAGRAM_HANDLE}</p>
              <p className="mt-1.5 font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-400" data-testid="portfolio-count">{tiles.length} projects · Sydney &amp; NSW · Built on lines. Perfected on courts.</p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <a href={INSTAGRAM_URL} target="_blank" rel="me noopener noreferrer" data-testid="portfolio-follow"
               className="inline-flex items-center gap-2 rounded-sm border border-gold/50 px-4 py-2.5 text-sm font-bold text-gold transition-colors hover:bg-gold hover:text-obsidian">
              <Instagram size={15} /> Follow
            </a>
            <a href={FACEBOOK_URL} target="_blank" rel="me noopener noreferrer" data-testid="portfolio-follow-facebook"
               className="inline-flex items-center gap-2 rounded-sm border border-gold/50 px-4 py-2.5 text-sm font-bold text-gold transition-colors hover:bg-gold hover:text-obsidian">
              <Facebook size={15} /> Follow us on Facebook
            </a>
          </div>
        </div>
        <div className="mt-2 grid grid-cols-3 gap-1 md:grid-cols-4 md:gap-2" data-testid="portfolio-grid">
          {tiles.map((t, i) => <Tile key={t.key} t={t} i={i} big={i === 0} />)}
        </div>
      </section>

      <section className="border-y border-gold/30 bg-obsidian-800 py-16" data-testid="portfolio-points">
        <div className="mx-auto grid max-w-7xl gap-10 px-5 md:grid-cols-2 md:px-10">
          <Reveal>
            <Overline className="text-gold">What {aud ? v.label.toLowerCase() : "every client"} get{aud ? "" : "s"}</Overline>
            <h2 className="mt-4 font-heading font-black text-3xl tracking-tight md:text-5xl">ONE CREW. <span className="gold-text">ONE STANDARD.</span></h2>
            <ul className="mt-6 space-y-3">
              {v.points.map((pt) => <li key={pt} className="flex gap-3 text-zinc-200"><Star size={16} className="mt-1 shrink-0 text-gold" />{pt}</li>)}
            </ul>
            <div className="mt-8 flex flex-wrap gap-2 font-mono text-[10px] uppercase tracking-widest text-zinc-400">
              <span className="inline-flex items-center gap-1.5 rounded-full border border-white/12 px-3 py-1.5"><ShieldCheck size={12} className="text-gold" /> Cm3 prequalified</span>
              <span className="inline-flex items-center gap-1.5 rounded-full border border-white/12 px-3 py-1.5"><BadgeCheck size={12} className="text-gold" /> $20M public liability</span>
              <span className="inline-flex items-center gap-1.5 rounded-full border border-white/12 px-3 py-1.5"><MapPin size={12} className="text-gold" /> 545 Merrylands Rd, Merrylands</span>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <Overline className="text-gold">Google reviews · Speedy Line Marking</Overline>
            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              {TESTIMONIALS.map((t, i) => (
                <figure key={t.name} className="glass rounded-lg p-5" data-testid={`portfolio-review-${i}`}>
                  <div className="text-neon tracking-[0.2em] text-xs" aria-label="5 star review">★★★★★</div>
                  <blockquote className="mt-3 text-sm text-zinc-300">"{t.quote}"</blockquote>
                  <figcaption className="mt-3 font-heading font-bold text-xs text-gold">{t.name}</figcaption>
                </figure>
              ))}
            </div>
            <p className="mt-3 font-mono text-[10px] text-zinc-600">Real Google reviews of the Speedy Line Marking team — the crew behind Speedy Resurfacing.</p>
          </Reveal>
        </div>
      </section>

      <section id="enquire" className="mx-auto max-w-7xl px-5 py-20 md:px-10" data-testid="portfolio-enquire">
        <Overline className="text-gold">Your court next</Overline>
        <h2 className="mt-4 mb-8 font-heading font-black text-3xl tracking-tight md:text-5xl">BOOK A FREE SITE ASSESSMENT. <span className="gold-text">THREE DETAILS.</span></h2>
        <AssessmentForm testid="portfolio-form" source={audKey ? `portfolio-${audKey}` : "portfolio"} />
        <Link to="/gallery" className="mt-8 inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400 hover:text-gold" data-testid="portfolio-archive-link">
          Browse the full Court Archive <ArrowUpRight size={13} />
        </Link>
      </section>
    </div>
  );
}
